import { formatDateTime, SituacaoInterconsulta } from '../../../../../../_metronic/helpers'
import { StatusDotTone } from '../../../../../components'
import { InterconsultaSolicitanteList } from './_models'

/**
 * Rótulo e tom de cada situação. Rótulos iguais às descrições do catálogo do backend
 * (/tipos/interconsulta/situacao), aqui estáticos porque a célula precisa deles sem esperar
 * requisição; o filtro usa o catálogo.
 */
export const SITUACAO_INTERCONSULTA: Record<
  SituacaoInterconsulta,
  { label: string; tone: StatusDotTone }
> = {
  [SituacaoInterconsulta.Aguardando]: { label: 'Aguardando teleconsultor', tone: 'info' },
  [SituacaoInterconsulta.EmAndamento]: { label: 'Em andamento', tone: 'primary' },
  [SituacaoInterconsulta.Pendente]: { label: 'Pendente com o solicitante', tone: 'warning' },
  [SituacaoInterconsulta.EmReplica]: { label: 'Réplica aguardando resposta', tone: 'info' },
  [SituacaoInterconsulta.Concluida]: { label: 'Concluída', tone: 'success' },
  [SituacaoInterconsulta.Avaliada]: { label: 'Concluída e avaliada', tone: 'success' },
  [SituacaoInterconsulta.Cancelada]: { label: 'Cancelada', tone: 'danger' },
}

/** PDF da teleconsultoria só existe com o parecer assinado: CONCLUIDA ou AVALIADA. */
export const teleconsultoriaDisponivel = (situacao?: SituacaoInterconsulta) =>
  situacao === SituacaoInterconsulta.Concluida || situacao === SituacaoInterconsulta.Avaliada

export const isPendente = (linha: InterconsultaSolicitanteList) =>
  linha.situacaoInterconsulta === SituacaoInterconsulta.Pendente

/**
 * O que o solicitante pode (ou precisa) fazer na linha. Réplica e desfecho dependem dos prazos
 * calculados pelo backend (`interconsulta`).
 */
export const acoesSolicitante = (linha: InterconsultaSolicitanteList) => ({
  resolverPendencia: isPendente(linha),
  replicar: !!linha.interconsulta?.podeReplicar,
  avaliar: !!linha.interconsulta?.podeAvaliar,
})

/**
 * Ações esperadas do solicitante, uma por linha, para destacar a linha da tabela; vazio quando
 * não há ação.
 */
export const acoesPendentesSolicitante = (linha: InterconsultaSolicitanteList): string[] => {
  const { resolverPendencia, replicar, avaliar } = acoesSolicitante(linha)
  if (resolverPendencia) return ['Ação necessária: resolver a pendência']

  // Prazo de réplica que já fechou também aparece: sem ele, "Concluída" sem a ação "Replicar"
  // parece defeito, e o motivo é só o prazo de 24h a partir da assinatura do parecer.
  const replicaEncerrada =
    !replicar && !linha.interconsulta?.avaliada && !!linha.interconsulta?.prazoReplica

  return [
    replicar && `Réplica até ${formatDateTime(linha.interconsulta?.prazoReplica)}`,
    replicaEncerrada &&
      `Prazo de réplica encerrado em ${formatDateTime(linha.interconsulta?.prazoReplica)}`,
    avaliar && `Desfecho até ${formatDateTime(linha.interconsulta?.prazoAvaliacao)}`,
  ].filter((acao): acao is string => Boolean(acao))
}
